import React, { useEffect, useMemo, useState } from "react";
import { Masters } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "./ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Tag, Network, Building2, FileBadge, Shield, Wrench, Headphones, Bike, Map as MapIcon, Globe, Power, Search } from "lucide-react";
import { cn } from "../lib/utils";

// Master types grouped into functional buckets — keeps the sidebar readable
// as more types are added. Order within a group is the display order in the
// left rail.
const GROUPS = [
  {
    key: "geo", label: "Geography",
    items: [
      { key: "state",              label: "State",              icon: Globe,       hasParent: false, hasCode: false, hasPrice: false, placeholder: "e.g. Odisha" },
      { key: "district",           label: "District",           icon: MapIcon,     hasParent: false, hasCode: false, hasPrice: false, placeholder: "e.g. Koraput" },
    ],
  },
  {
    key: "sales", label: "Sales",
    items: [
      { key: "enquiry_source",     label: "Enquiry Source",     icon: Tag,         hasParent: false, hasCode: false, hasPrice: false, placeholder: "e.g. Digital · Aggregator" },
      { key: "enquiry_sub_source", label: "Enquiry Sub-Source", icon: Network,     hasParent: true,  hasCode: false, hasPrice: false, placeholder: "e.g. Social Media" },
      { key: "vehicle_model",      label: "Vehicle Model",      icon: Bike,        hasParent: false, hasCode: false, hasPrice: false, placeholder: "e.g. R15 V4 / MT-15" },
    ],
  },
  {
    key: "finance", label: "Finance & Regn",
    items: [
      { key: "finance_company",    label: "Finance Company",    icon: Building2,   hasParent: false, hasCode: false, hasPrice: false, placeholder: "e.g. HDFC Bank" },
      { key: "regn_type",          label: "Registration Type",  icon: FileBadge,   hasParent: false, hasCode: true,  hasPrice: false, placeholder: "e.g. Odisha Registration" },
    ],
  },
  {
    key: "vas", label: "Value-Added Services",
    items: [
      { key: "vas_protection_plus",label: "Protection Plus",    icon: Shield,      hasParent: false, hasCode: false, hasPrice: true,  placeholder: "e.g. PP 1 Year Silver" },
      { key: "vas_amc",            label: "AMC Plan",           icon: Wrench,      hasParent: false, hasCode: false, hasPrice: true,  placeholder: "e.g. AMC 3 Year" },
      { key: "vas_rsa",            label: "RSA Plan",           icon: Headphones,  hasParent: false, hasCode: false, hasPrice: true,  placeholder: "e.g. RSA Gold" },
    ],
  },
];
const ALL_TYPES = GROUPS.flatMap((g) => g.items);
const META_BY_KEY = Object.fromEntries(ALL_TYPES.map((t) => [t.key, t]));

export default function MastersPanel({ canDelete = true, extraGroups = [] }) {
  // Custom groups (Locations, Users, etc.) render arbitrary React nodes in the
  // right pane instead of the generic MasterTypeManager. Each item has:
  //   { key, label, icon, count?, render: () => JSX }
  const customItems = useMemo(
    () => extraGroups.flatMap((g) => g.items),
    [extraGroups],
  );
  const customByKey = useMemo(
    () => Object.fromEntries(customItems.map((t) => [t.key, t])),
    [customItems],
  );

  // First custom item takes priority as the default active tab so admins land
  // on the most operationally relevant screen (Locations / Users) instead of
  // "State".
  const defaultActive = customItems[0]?.key || "state";
  const [active, setActive] = useState(defaultActive);
  const [counts, setCounts] = useState({});

  // Fetch counts once on mount so the sidebar shows a size indicator per
  // master type. Refreshed after every mutation via the child callback.
  const refreshCounts = async () => {
    const results = await Promise.all(ALL_TYPES.map((t) => Masters.list({ type: t.key })));
    setCounts(Object.fromEntries(ALL_TYPES.map((t, i) => [t.key, results[i].length])));
  };
  useEffect(() => { refreshCounts(); }, []);

  const meta = META_BY_KEY[active];
  const customActive = customByKey[active];
  const groups = [...extraGroups, ...GROUPS];

  return (
    <Card className="border-slate-200">
      <CardHeader className="pb-3">
        <CardTitle className="font-display text-lg">Masters</CardTitle>
        <p className="text-xs text-slate-500">
          Manage dropdown data used across the app. Inactive entries are hidden from Sales Form dropdowns but preserved for historical reporting.
        </p>
      </CardHeader>
      <CardContent className="p-0">
        <div className="grid grid-cols-1 lg:grid-cols-[13rem_minmax(0,1fr)]">
          {/* Left rail — categories */}
          <nav
            className="border-b border-slate-200 bg-slate-50/70 p-3 lg:border-b-0 lg:border-r"
            data-testid="masters-sidebar"
          >
            <div className="space-y-4">
              {groups.map((g) => (
                <div key={g.key}>
                  <div className="mb-1.5 px-2 text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500">
                    {g.label}
                  </div>
                  <ul className="space-y-0.5">
                    {g.items.map((t) => {
                      const Icon = t.icon;
                      const isActive = active === t.key;
                      const isCustom = customByKey[t.key] != null;
                      const displayCount = isCustom ? t.count : counts[t.key];
                      return (
                        <li key={t.key}>
                          <button
                            type="button"
                            onClick={() => setActive(t.key)}
                            data-testid={`master-nav-${t.key}`}
                            className={cn(
                              "flex w-full items-center justify-between gap-2 rounded-md px-2 py-1.5 text-left text-xs font-medium transition-colors",
                              isActive
                                ? "bg-primary text-primary-foreground"
                                : "text-slate-700 hover:bg-white hover:text-primary",
                            )}
                          >
                            <span className="flex items-center gap-1.5 truncate">
                              <Icon className="h-3.5 w-3.5 flex-shrink-0" />
                              <span className="truncate">{t.label}</span>
                            </span>
                            {displayCount != null && (
                              <span className={cn(
                                "flex-shrink-0 rounded-full px-1.5 py-0 font-mono-data text-[10px] font-bold",
                                isActive ? "bg-white/25" : "bg-slate-200 text-slate-700",
                              )}>
                                {displayCount}
                              </span>
                            )}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))}
            </div>
          </nav>

          {/* Right pane — active master manager */}
          <div className="p-4">
            {customActive
              ? customActive.render()
              : <MasterTypeManager meta={meta} onMutate={refreshCounts} canDelete={canDelete} />}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function MasterTypeManager({ meta, onMutate, canDelete = true }) {
  const [items, setItems] = useState([]);
  const [parents, setParents] = useState([]);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [price, setPrice] = useState("");
  const [parentId, setParentId] = useState("");
  const [isActive, setIsActive] = useState(true);

  const refresh = async () => {
    const list = await Masters.list({ type: meta.key });
    setItems(list);
    if (meta.hasParent) {
      const ps = await Masters.list({ type: "enquiry_source" });
      setParents(ps);
    }
  };

  useEffect(() => { setSearch(""); refresh(); /* eslint-disable-next-line */ }, [meta.key]);

  const parentMap = useMemo(
    () => Object.fromEntries(parents.map((p) => [p.id, p.name])),
    [parents],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((m) =>
      (m.name || "").toLowerCase().includes(q)
      || (m.code || "").toLowerCase().includes(q),
    );
  }, [items, search]);

  const reset = () => {
    setEditingId(null); setName(""); setCode(""); setPrice(""); setParentId(""); setIsActive(true);
  };

  const openAdd = () => { reset(); setOpen(true); };
  const openEdit = (m) => {
    setEditingId(m.id);
    setName(m.name || "");
    setCode(m.code || "");
    setPrice(m.price != null ? String(m.price) : "");
    setParentId(m.parent_id || "");
    setIsActive(m.is_active !== false);
    setOpen(true);
  };

  const save = async () => {
    if (!name.trim()) return toast.error("Name is required");
    if (meta.hasParent && !parentId) return toast.error("Select an Enquiry Source");
    try {
      const payload = {
        name: name.trim(),
        code: meta.hasCode && code.trim() ? code.trim() : null,
        price: meta.hasPrice && price !== "" ? Number(price) : null,
        parent_id: meta.hasParent ? parentId : null,
        is_active: isActive,
      };
      if (editingId) {
        await Masters.update(editingId, payload);
        toast.success(`${meta.label} updated`);
      } else {
        await Masters.create({ type: meta.key, ...payload });
        toast.success(`${meta.label} added`);
      }
      setOpen(false); reset();
      await refresh();
      onMutate?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const toggleActive = async (m) => {
    try {
      await Masters.update(m.id, { is_active: !(m.is_active !== false) });
      toast.success(m.is_active === false ? `${meta.label} activated` : `${meta.label} deactivated`);
      await refresh(); onMutate?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm(`Delete this ${meta.label}? Historical references will still keep the value.`)) return;
    try {
      await Masters.remove(id);
      toast.success("Removed");
      await refresh(); onMutate?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const Icon = meta.icon;

  return (
    <div className="space-y-3" data-testid={`master-panel-${meta.key}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Icon className="h-5 w-5 text-primary" />
          <div>
            <div className="font-display text-base font-semibold text-slate-900">{meta.label}</div>
            <div className="text-[11px] text-slate-500">
              {items.length} total · {items.filter((m) => m.is_active !== false).length} active
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search…"
              className="h-8 w-44 pl-7 text-xs"
              data-testid={`master-search-${meta.key}`}
            />
          </div>
          <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
            <DialogTrigger asChild>
              <Button size="sm" className="gap-1" data-testid={`add-${meta.key}-btn`} onClick={openAdd}>
                <Plus className="h-3.5 w-3.5" /> Add {meta.label}
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{editingId ? `Edit ${meta.label}` : `Add ${meta.label}`}</DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                {meta.hasParent && (
                  <div className="space-y-1.5">
                    <Label className="text-xs">Enquiry Source *</Label>
                    <Select value={parentId} onValueChange={setParentId}>
                      <SelectTrigger data-testid={`${meta.key}-parent-select`}>
                        <SelectValue placeholder="Select parent source" />
                      </SelectTrigger>
                      <SelectContent>
                        {parents.length === 0 && (
                          <div className="px-3 py-2 text-xs text-slate-500">
                            Add an Enquiry Source first
                          </div>
                        )}
                        {parents.map((p) => (
                          <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div className="space-y-1.5">
                  <Label className="text-xs">Name *</Label>
                  <Input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    data-testid={`${meta.key}-name-input`}
                    placeholder={meta.placeholder}
                  />
                </div>
                {meta.hasCode && (
                  <div className="space-y-1.5">
                    <Label className="text-xs">Code (e.g. OD)</Label>
                    <Input
                      value={code}
                      onChange={(e) => setCode(e.target.value.toUpperCase())}
                      maxLength={6}
                      data-testid={`${meta.key}-code-input`}
                    />
                  </div>
                )}
                {meta.hasPrice && (
                  <div className="space-y-1.5">
                    <Label className="text-xs">Price (₹)</Label>
                    <Input
                      type="number"
                      value={price}
                      onChange={(e) => setPrice(e.target.value)}
                      data-testid={`${meta.key}-price-input`}
                      placeholder="e.g. 1500"
                    />
                  </div>
                )}
                <div className="flex items-center justify-between rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
                  <div>
                    <Label className="text-xs font-semibold">Active</Label>
                    <p className="text-[10px] text-slate-500">Inactive entries are hidden from Sales Form dropdowns.</p>
                  </div>
                  <Switch
                    checked={isActive}
                    onCheckedChange={setIsActive}
                    data-testid={`${meta.key}-active-toggle`}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button onClick={save} data-testid={`save-${meta.key}-btn`}>
                  {editingId ? "Save Changes" : "Save"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="rounded-md border border-slate-200 bg-white">
        {filtered.length === 0 ? (
          <div className="px-5 py-8 text-center text-sm text-slate-500">
            {search
              ? <>No matches for &ldquo;{search}&rdquo;</>
              : <>No {meta.label.toLowerCase()} yet. Click <strong>Add {meta.label}</strong> to create one.</>}
          </div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {filtered.map((m) => {
              const inactive = m.is_active === false;
              return (
                <li
                  key={m.id}
                  className={cn(
                    "flex items-center justify-between gap-3 px-4 py-2.5",
                    inactive && "bg-slate-50/60",
                  )}
                  data-testid={`master-row-${m.id}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 text-sm font-medium">
                      <span className={inactive ? "text-slate-500 line-through" : "text-slate-900"}>
                        {m.name}
                      </span>
                      {inactive && (
                        <span
                          className="rounded bg-slate-200 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-slate-700"
                          data-testid={`master-inactive-${m.id}`}
                        >
                          Inactive
                        </span>
                      )}
                    </div>
                    {(m.code || (meta.hasParent && parentMap[m.parent_id]) || (meta.hasPrice && m.price != null)) && (
                      <div className="text-xs text-slate-500">
                        {m.code && <span className="font-mono-data">{m.code}</span>}
                        {m.code && (meta.hasParent || meta.hasPrice) && " · "}
                        {meta.hasParent && parentMap[m.parent_id] && (
                          <span>under <em>{parentMap[m.parent_id]}</em></span>
                        )}
                        {meta.hasPrice && m.price != null && (
                          <span>₹{Number(m.price).toLocaleString("en-IN")}</span>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      size="sm" variant="ghost"
                      onClick={() => toggleActive(m)}
                      className={cn(
                        "h-8 gap-1 px-2 text-[11px]",
                        inactive ? "text-emerald-700 hover:bg-emerald-50" : "text-slate-500 hover:bg-amber-50 hover:text-amber-700",
                      )}
                      title={inactive ? "Activate" : "Deactivate"}
                      data-testid={`toggle-master-${m.id}`}
                    >
                      <Power className="h-3.5 w-3.5" />
                      {inactive ? "Activate" : "Deactivate"}
                    </Button>
                    <Button
                      size="icon" variant="ghost"
                      onClick={() => openEdit(m)}
                      className="h-8 w-8 text-slate-400 hover:text-primary"
                      data-testid={`edit-master-${m.id}`}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    {canDelete && (
                      <Button
                        size="icon" variant="ghost"
                        onClick={() => remove(m.id)}
                        className="h-8 w-8 text-slate-400 hover:text-red-600"
                        data-testid={`delete-master-${m.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
